import { getHttpClient } from '@spcc/core'
import type {
  SubCustomerDetail,
  SubCustomerExportRow,
  SubCustomerOptions,
  SubCustomerRef,
  SubCustomerRequest,
  SubCustomerTop,
} from '../../domain/types'
import { SUB_CUSTOMER_DASHBOARD_ENDPOINTS as E, SUB_CUSTOMER_SEARCH_PAGE_SIZE } from '../../domain/constants'
import { buildParams, buildUrl } from './sales-dashboard-api'

type WindowRequest = Pick<SubCustomerRequest, 'from' | 'to'> & Partial<SubCustomerRequest>

function toParams(request: WindowRequest, locale: string): URLSearchParams {
  const params = buildParams(request)
  if (request.subCustomerId) params.set('subCustomerId', request.subCustomerId)
  params.set('locale', locale)
  return params
}

async function get<T>(endpoint: string, params: URLSearchParams): Promise<T> {
  const response = await getHttpClient().get<{ data: T }>(buildUrl(endpoint, params))
  // Strapi wraps the payload in { data } — same unwrap as salesDashboardApi.
  return ((response.data as { data?: T })?.data ?? response.data) as T
}

function buildSearchParams(query: string, customerIds?: string[]): URLSearchParams {
  const params = new URLSearchParams()
  params.set('filters[$or][0][name][$containsi]', query)
  params.set('filters[$or][1][code][$containsi]', query)
  params.set('filters[$or][2][phone][$containsi]', query)
  customerIds?.forEach((id, i) => params.set(`filters[tenant][documentId][$in][${i}]`, id))
  params.set('pagination[pageSize]', String(SUB_CUSTOMER_SEARCH_PAGE_SIZE))
  params.set('sort', 'name:asc')
  params.set('populate[tenant][fields][0]', 'name')
  return params
}

interface SubCustomerDto {
  documentId: string
  name: string
  code?: string | null
  customerType?: string | null
  tenant?: { name?: string | null } | null
}

function toSubCustomerRef(dto: SubCustomerDto): SubCustomerRef {
  return {
    id: dto.documentId,
    name: dto.name,
    code: dto.code ?? null,
    customerName: dto.tenant?.name ?? null,
    isPlaceholder: dto.customerType === 'placeholder',
  }
}

export const subCustomerDashboardApi = {
  getTop: (request: SubCustomerRequest, locale: string) => get<SubCustomerTop>(E.TOP, toParams(request, locale)),
  getDetail: (request: SubCustomerRequest, locale: string) => get<SubCustomerDetail>(E.DETAIL, toParams(request, locale)),
  getOptions: (request: WindowRequest, locale: string) => get<SubCustomerOptions>(E.OPTIONS, toParams(request, locale)),

  async getExportRows(request: SubCustomerRequest, locale: string): Promise<SubCustomerExportRow[]> {
    const data = await get<{ rows?: SubCustomerExportRow[] }>(E.EXPORT, toParams(request, locale))
    return data?.rows ?? []
  },

  /** Reuses the tenant-isolated sub-customer list API (soft-deleted rows are excluded server-side). */
  async search(query: string, customerIds?: string[]): Promise<SubCustomerRef[]> {
    const data = await get<SubCustomerDto[]>(E.SEARCH, buildSearchParams(query, customerIds))
    return Array.isArray(data) ? data.map(toSubCustomerRef) : []
  },
}
